import { getConfig } from '@/config/runtimeConfig';
import { authEvents } from '@/lib/authEvents';
import { getAuthToken } from '@/lib/devStackAuth.util';
// GW-6534: Read current org id from the Zustand store (single source of
// truth). The localStorage write side in `organizationSlice.ts` remains as a
// one-way mirror for any non-React consumer not yet migrated. Reading
// directly from localStorage caused the GW-6372 cross-user contamination
// window between signIn (which clears Zustand) and selectOrganization (which
// repopulates Zustand and localStorage).
import { getCurrentOrgIdFromStore } from '@/stores/orgIdAccessor';
import { isFailedToFetchMessage } from '@/lib/networkError.util';

import { ApiError } from './apiError';

/**
 * GW-10371 / GW-5968: The ONLY 403 response bodies that represent a genuine
 * invalid/expired token (and therefore SHOULD sign the user out). These are the
 * NARROW, VERIFIED AWS API Gateway token-rejection shapes:
 *   - Gateway responses configured in
 *     gw-functions/terraform/modules/api-gateway/main.tf:120-163
 *     (UNAUTHORIZED -> {"message":"Unauthorized"}, EXPIRED_TOKEN ->
 *     {"message":"The incoming token has expired"}, ACCESS_DENIED ->
 *     "User is not authorized to access this resource...").
 *   - The custom TOKEN authorizer at gw-functions/functions/authorizer/main.go:86
 *     whose rejection string is exactly "Unauthorized".
 *
 * Matching is EXACT (whole-string), never substring: application-level denials
 * embed these words inside longer sentences (e.g. rbac.go "Access denied:
 * requires ...", httpmiddleware/identity.go "Forbidden: You are not a member of
 * the requested organization"). Those keep the user signed in — only a bare,
 * whole-string AWS token token is a real dead-token signal.
 */
const TOKEN_INVALID_403_MESSAGES: readonly string[] = [
  'Forbidden',
  'Unauthorized',
  'The incoming token has expired',
  'User is not authorized to access this resource',
];

interface BaseApiClientOptions {
  baseURL?: string;
  /**
   * GW-6394: Factory that returns the base URL. Use instead of `baseURL` when the
   * URL is derived from runtime config, so evaluation is deferred to the first API call
   * rather than module load time. This prevents the "Runtime config not available" crash
   * that occurs when a module-level singleton is instantiated before env-config.js runs.
   *
   * @example
   *   super({ baseURLFactory: () => getConfig().sdkApiBaseUrl ?? getConfig().apiBaseUrl });
   */
  baseURLFactory?: () => string;
  requiresAuth?: boolean;
  suppressSessionExpired?: boolean;
}

export interface RequestOptions {
  customAuthToken?: string;
  signal?: AbortSignal;
  /**
   * GW-10374: Suppress auth for a single request even when the client is
   * configured with `requiresAuth: true`. When set, `getHeaders` omits the
   * `Authorization` and `X-Organization-Id` headers, and a 401/403 on the
   * request does NOT emit `session-expired` (so it cannot sign the user out).
   *
   * Use for endpoints that are public at the API Gateway level but live on a
   * client that also performs authenticated calls — e.g. the public
   * marketplace `GET /v1/services/{slug}` on `ServicesApiClient`, which
   * otherwise attached a stale token and logged the user out on a 403.
   */
  skipAuth?: boolean;
}

export abstract class BaseApiClient {
  /**
   * GW-6394: Stores either the resolved URL string (when an explicit literal URL is
   * provided) or null (when deferred via factory or default config lookup).
   * Access the URL through the `baseURL` getter — never read this field directly.
   */
  private _baseURL: string | null;
  /**
   * GW-6394: Optional factory for lazy URL resolution.
   * Set when a subclass passes `baseURLFactory` in options.
   */
  private _baseURLFactory: (() => string) | null;
  private requiresAuth: boolean;
  private suppressSessionExpired: boolean;

  constructor(options?: BaseApiClientOptions | string) {
    if (typeof options === 'string') {
      // Explicit literal URL — store immediately, no config call needed.
      this._baseURL = options.endsWith('/') ? options.slice(0, -1) : options;
      this._baseURLFactory = null;
      this.requiresAuth = true;
      this.suppressSessionExpired = false;
    } else if (options?.baseURL) {
      // Explicit baseURL in options — store immediately, no config call needed.
      const url = options.baseURL;
      this._baseURL = url.endsWith('/') ? url.slice(0, -1) : url;
      this._baseURLFactory = null;
      this.requiresAuth = options.requiresAuth ?? true;
      this.suppressSessionExpired = options.suppressSessionExpired ?? false;
    } else if (options?.baseURLFactory) {
      // GW-6394: Factory provided — defer URL resolution until first API call.
      this._baseURL = null;
      this._baseURLFactory = options.baseURLFactory;
      this.requiresAuth = options.requiresAuth ?? true;
      this.suppressSessionExpired = options.suppressSessionExpired ?? false;
    } else {
      // GW-6394: No URL provided — defer getConfig() until first API call to prevent
      // "Runtime config not available" crash when a module-level singleton is evaluated
      // before env-config.js executes (e.g. on lazy-loaded chunks).
      this._baseURL = null;
      this._baseURLFactory = null;
      this.requiresAuth = options?.requiresAuth ?? true;
      this.suppressSessionExpired = options?.suppressSessionExpired ?? false;
    }
  }

  /**
   * GW-6394: Lazily resolved base URL.
   * - Explicit URL (string option or baseURL option): returned immediately from cache.
   * - Factory (baseURLFactory option): factory is called once on first access, result cached.
   * - No URL: getConfig().apiBaseUrl is read on first access, result cached.
   */
  protected get baseURL(): string {
    if (this._baseURL === null) {
      const url = this._baseURLFactory ? this._baseURLFactory() : getConfig().apiBaseUrl;
      this._baseURL = url.endsWith('/') ? url.slice(0, -1) : url;
    }
    return this._baseURL;
  }

  /**
   * Normalize an API path to prevent duplicate /v1 segments.
   * If baseURL already includes /v1, strips /v1 prefix from paths.
   * @see GW-327 - Original fix for duplicate /v1
   */
  private normalizePath(path: string): string {
    let normalized = path.startsWith('/') ? path : `/${path}`;
    if (this.baseURL.endsWith('/v1') && normalized.startsWith('/v1/')) {
      normalized = normalized.slice(3); // Remove '/v1' prefix, keep the rest
    } else if (this.baseURL.endsWith('/v1') && normalized === '/v1') {
      normalized = '/';
    }
    return normalized;
  }

  private getCurrentOrgId(): string | null {
    // GW-6534: Source of truth is the Zustand store. The previous
    // localStorage-only read drifted from the store during the post-signIn /
    // pre-selectOrganization window — see GW-6372. Falls back to localStorage
    // when the store has not yet been initialized (e.g. a non-React consumer
    // running before app bootstrap).
    return getCurrentOrgIdFromStore() ?? localStorage.getItem('gw-current-org-id');
  }

  protected async getHeaders(options?: RequestOptions): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // GW-10374: `skipAuth` lets a single request opt out of auth even on a
    // client whose class-level `requiresAuth` is true (the client still does
    // authenticated calls elsewhere). Used for public marketplace reads.
    if (this.requiresAuth && !options?.skipAuth) {
      const token = options?.customAuthToken ?? (await getAuthToken());
      const orgId = this.getCurrentOrgId();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (orgId) headers['X-Organization-Id'] = orgId;
    }

    return headers;
  }

  /**
   * GW-8963: Wrap the raw `fetch` call so a transient network failure surfaces as an
   * `ApiError` instead of an uncaught `TypeError('Failed to fetch')` that escapes to
   * `window.onunhandledrejection` → Sentry (dup issues GW-SPA-2F / GW-SPA-2G).
   *
   * The browser throws `TypeError: Failed to fetch` on network blips, navigation away
   * mid-request, and CORS/401 responses from API Gateway. `organizationSlice.ts:257`
   * already classifies this exact case as a benign transient network error; this mirrors
   * that classification for every verb that goes through the base client. Any other error
   * (including other `TypeError` messages) is re-thrown unchanged so real bugs propagate.
   *
   * GW-9113: Classification is prefix-based (via `isFailedToFetchMessage`) rather than an
   * exact `===` match, so browser variants that append the failing URL to the message are
   * also caught.
   */
  private async fetchWithNetworkGuard(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    try {
      return await fetch(input, init);
    } catch (error) {
      if (error instanceof TypeError && isFailedToFetchMessage(error.message)) {
        throw new ApiError('Network unavailable: Failed to fetch', undefined, {
          __requestUrl: typeof input === 'string' ? input : input.toString(),
          __networkError: true,
        });
      }
      throw error;
    }
  }

  private async throwApiError(response: Response): Promise<never> {
    const errorData = await response.json().catch(() => ({}));
    // GW-6428: Attach the request URL so Sentry's beforeSend can fingerprint
    // by (status, normalized route) instead of collapsing every API error
    // into a single group via the shared throwApiError stack frame.
    const enriched = { ...errorData, __requestUrl: response.url };
    throw new ApiError(
      errorData.error || errorData.message || `API error: ${response.statusText}`,
      response.status,
      enriched
    );
  }

  /**
   * GW-6128: Retry a request with a force-refreshed token.
   * Used when the server returns 403 NO_ORG_CONTEXT due to stale JWT claims.
   * @param method HTTP method
   * @param path API endpoint path
   * @param options Request options
   * @param body Optional request body
   * @returns The retry response
   */
  private async retryWithRefreshedToken(
    method: string,
    path: string,
    options?: RequestOptions,
    body?: unknown
  ): Promise<Response> {
    // Force-refresh the token from Cognito to pick up new claims (e.g. custom:orgId after membership change)
    const freshToken = await getAuthToken({ forceRefresh: true });
    const headers = await this.getHeaders(options);
    headers['Authorization'] = `Bearer ${freshToken}`;

    return this.fetchWithNetworkGuard(`${this.baseURL}${this.normalizePath(path)}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * GW-10371 / GW-5968: Decide whether a 403 represents a genuine invalid/expired
   * token (log the user out) rather than an application-level denial (keep them
   * signed in). This is the ONE place that decision is made; both handleResponse
   * and getBlob call it so they cannot diverge.
   *
   * Returns true ONLY when:
   *   1. requiresAuth was in effect but NO token was actually attached to the
   *      request (the auth provider returned nothing) — the session is dead, OR
   *   2. the body is a bare, whole-string AWS API Gateway token-rejection shape
   *      (see TOKEN_INVALID_403_MESSAGES). Matching is EXACT on either the
   *      `error` or `message` field — an application 403 that merely CONTAINS
   *      "Forbidden"/"Unauthorized" inside a longer sentence does NOT match.
   */
  private isTokenInvalid403(
    errorData: { error?: unknown; message?: unknown },
    tokenWasSent: boolean
  ): boolean {
    if (!tokenWasSent) return true;
    return (
      (typeof errorData.error === 'string' &&
        TOKEN_INVALID_403_MESSAGES.includes(errorData.error)) ||
      (typeof errorData.message === 'string' &&
        TOKEN_INVALID_403_MESSAGES.includes(errorData.message))
    );
  }

  /**
   * GW-10371: Single chokepoint for the session-expired decision, shared by
   * handleResponse and getBlob.
   *
   * - A 401 ALWAYS signs the user out (unchanged — session-level failure).
   * - A 403 signs the user out ONLY when it is a genuine token-invalid response
   *   (isTokenInvalid403). This inverts the previous over-broad default where
   *   every 403 logged out unless it matched a growing allowlist.
   *
   * Emission is always gated by the client-level opt-outs (requiresAuth,
   * suppressSessionExpired) and the per-request skipAuth flag.
   */
  private maybeEmitSessionExpired(
    status: number,
    errorData: { error?: unknown; message?: unknown },
    options: RequestOptions | undefined,
    tokenWasSent: boolean
  ): void {
    if (!this.requiresAuth || this.suppressSessionExpired || options?.skipAuth) {
      return;
    }
    const isAuthFailure =
      status === 401 || (status === 403 && this.isTokenInvalid403(errorData, tokenWasSent));
    if (isAuthFailure) {
      authEvents.emit('session-expired');
    }
  }

  private async handleResponse<T>(
    response: Response,
    retryContext?: { method: string; path: string; options?: RequestOptions; body?: unknown },
    tokenWasSent = true
  ): Promise<T> {
    if (!response.ok) {
      // GW-6160: Parse body first to check for NO_ORG_CONTEXT before emitting session-expired.
      // Use clone() to avoid consuming the body before throwApiError reads it.
      // Defensive: check for clone() to support test mocks that don't implement it.
      const cloned = typeof response.clone === 'function' ? response.clone() : response;
      const errorData = await cloned.json().catch(() => ({}));
      const isNoOrgContext = response.status === 403 && errorData.code === 'NO_ORG_CONTEXT';

      // GW-6128: On first 403 NO_ORG_CONTEXT, force-refresh the token and retry once.
      // This handles stale JWT claims (e.g., user added to org but token pre-dates membership).
      // Only retry if we have retry context (method/path) and this is the first attempt.
      if (isNoOrgContext && retryContext) {
        const retryResponse = await this.retryWithRefreshedToken(
          retryContext.method,
          retryContext.path,
          retryContext.options,
          retryContext.body
        );
        // Recursive call without retryContext to prevent infinite retry loop
        return this.handleResponse<T>(retryResponse);
      }

      // GW-10371 / GW-5968: A 401 always signs the user out; a 403 does so ONLY
      // when it is a genuine token-invalid response. Application-level 403s
      // (NO_ORG_CONTEXT, org mismatch, RBAC role denial, resource ownership,
      // participation, per-resource permission) keep the user signed in.
      this.maybeEmitSessionExpired(response.status, errorData, retryContext?.options, tokenWasSent);
      await this.throwApiError(response);
    }
    if (response.status === 204) return undefined as T;
    if (response.status === 202) {
      // 202 Accepted - async operation accepted, may have empty or minimal response
      const text = await response.text();
      return text ? JSON.parse(text) : ({} as T);
    }
    return response.json();
  }

  protected async get<T>(path: string, options?: RequestOptions): Promise<T> {
    const headers = await this.getHeaders(options);
    const response = await this.fetchWithNetworkGuard(
      `${this.baseURL}${this.normalizePath(path)}`,
      {
        method: 'GET',
        headers,
        signal: options?.signal,
      }
    );
    return this.handleResponse<T>(
      response,
      { method: 'GET', path, options },
      'Authorization' in headers
    );
  }

  protected async post<T>(path: string, data?: unknown, options?: RequestOptions): Promise<T> {
    const headers = await this.getHeaders(options);
    const response = await this.fetchWithNetworkGuard(
      `${this.baseURL}${this.normalizePath(path)}`,
      {
        method: 'POST',
        headers,
        body: data ? JSON.stringify(data) : undefined,
        signal: options?.signal,
      }
    );
    return this.handleResponse<T>(
      response,
      { method: 'POST', path, options, body: data },
      'Authorization' in headers
    );
  }

  protected async put<T>(path: string, data: unknown, options?: RequestOptions): Promise<T> {
    const headers = await this.getHeaders(options);
    const response = await this.fetchWithNetworkGuard(
      `${this.baseURL}${this.normalizePath(path)}`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      }
    );
    return this.handleResponse<T>(
      response,
      { method: 'PUT', path, options, body: data },
      'Authorization' in headers
    );
  }

  protected async patch<T>(path: string, data: unknown, options?: RequestOptions): Promise<T> {
    const headers = await this.getHeaders(options);
    const response = await this.fetchWithNetworkGuard(
      `${this.baseURL}${this.normalizePath(path)}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data),
      }
    );
    return this.handleResponse<T>(
      response,
      { method: 'PATCH', path, options, body: data },
      'Authorization' in headers
    );
  }

  protected async delete<T>(path: string, options?: RequestOptions): Promise<T> {
    const headers = await this.getHeaders(options);
    const response = await this.fetchWithNetworkGuard(
      `${this.baseURL}${this.normalizePath(path)}`,
      {
        method: 'DELETE',
        headers,
      }
    );
    return this.handleResponse<T>(
      response,
      { method: 'DELETE', path, options },
      'Authorization' in headers
    );
  }

  protected async getBlob(path: string, options?: RequestOptions): Promise<Blob> {
    const headers = await this.getHeaders(options);
    const tokenWasSent = 'Authorization' in headers;
    let response = await this.fetchWithNetworkGuard(`${this.baseURL}${this.normalizePath(path)}`, {
      method: 'GET',
      headers,
      signal: options?.signal,
    });

    if (!response.ok) {
      // GW-6160: Parse body first to check for NO_ORG_CONTEXT (same as handleResponse).
      // Defensive: check for clone() to support test mocks that don't implement it.
      const cloned = typeof response.clone === 'function' ? response.clone() : response;
      const errorData = await cloned.json().catch(() => ({}));
      const isNoOrgContext = response.status === 403 && errorData.code === 'NO_ORG_CONTEXT';

      // GW-6128: On first 403 NO_ORG_CONTEXT, force-refresh the token and retry once.
      // Same retry logic as handleResponse, adapted for Blob responses.
      if (isNoOrgContext) {
        response = await this.retryWithRefreshedToken('GET', path, options);
        // Re-check after retry — if still failing, make the SAME single decision
        // as handleResponse via the shared chokepoint, then throw.
        if (!response.ok) {
          const retryCloned = typeof response.clone === 'function' ? response.clone() : response;
          const retryErrorData = await retryCloned.json().catch(() => ({}));
          this.maybeEmitSessionExpired(response.status, retryErrorData, options, tokenWasSent);
          await this.throwApiError(response);
        }
      } else {
        // GW-10371: Identical decision to handleResponse — a 401 always signs the
        // user out; a 403 only when it is a genuine token-invalid response.
        this.maybeEmitSessionExpired(response.status, errorData, options, tokenWasSent);
        await this.throwApiError(response);
      }
    }

    if (response.status === 204) {
      throw new ApiError('No content available', 204);
    }
    return response.blob();
  }

  protected async upload<T>(path: string, formData: FormData): Promise<T> {
    const headers = await this.getHeaders();
    delete headers['Content-Type'];
    const response = await this.fetchWithNetworkGuard(
      `${this.baseURL}${this.normalizePath(path)}`,
      {
        method: 'POST',
        headers,
        body: formData,
      }
    );
    return this.handleResponse<T>(response, undefined, 'Authorization' in headers);
  }

  protected buildQueryString(
    params: Record<string, string | number | boolean | string[] | undefined>
  ): string {
    const filtered = Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .flatMap(([key, value]) => {
        if (Array.isArray(value)) {
          return value.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`);
        }
        return [`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`];
      });
    return filtered.length > 0 ? `?${filtered.join('&')}` : '';
  }
}
